import { inject } from "@angular/core";
import { ResolveFn } from "@angular/router";
import { EMPTY, Observable, of, timer } from "rxjs";
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
} from "rxjs/operators";
import { Store } from "@ngxs/store";
import { TranslocoService } from "@jsverse/transloco";
import { ToastrService } from "@sdk/lib/toastr/toastr.service";
import { AuthService } from "@auth0/auth0-angular";
import { GroupService } from "@sdk/lib/services/group.service";
import { PatientService } from "@sdk/lib/services/patient.service";
import {
  PrepareWebinarResult,
  WebinarService,
} from "@sdk/lib/services/webinar.service";
import { WEBINAR_ROUTE_PARAM } from "@sdk/lib/values/shared-paths";
import {
  AnalyticsService,
  EVENT_NAMES,
  WebinarPrepareLoaderService,
} from "@uplift-sdk";
import { FetchGroupAction } from "../actions/group.actions";
import {
  hasIndividualCourse,
  individualWebinarHolderId,
  patientOfWebinarHolder,
} from "@sdk/lib/functions/individual-course";

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 5000;

/**
 * Patient-side mirror of the therapist webinar prepare resolver.
 *
 * Ordering:
 *   1. Resolve the patient profile -> assigned_group_id (the patient
 *      route doesn't carry a group id).
 *   2. Call prepareWebinar, validate/repair, refresh state.
 *
 * On hard failure the resolver cancels navigation and the patient stays
 * on the source page with a toast explaining what to do next.
 */
function analyticsGroupId(holderId: string): string | undefined {
  return patientOfWebinarHolder(holderId) ? undefined : holderId;
}

export const webinarPrepareResolver: ResolveFn<
  PrepareWebinarResult | boolean
> = (route) => {
  const loader = inject(WebinarPrepareLoaderService);
  const webinarService = inject(WebinarService);
  const patientService = inject(PatientService);
  const groupService = inject(GroupService);
  const auth = inject(AuthService);
  const store = inject(Store);
  const toastr = inject(ToastrService);
  const transloco = inject(TranslocoService);
  const analytics = inject(AnalyticsService);

  const webinarId = route.paramMap.get(WEBINAR_ROUTE_PARAM);
  if (!webinarId) {
    return of(true);
  }

  loader.show();
  return auth.user$.pipe(
    filter((user) => !!user),
    take(1),
    switchMap((user) => patientService.getPatientProfileByUserID(user.sub)),
    first(),
    switchMap((profile) => {
      const assignedGroupId = (profile as any)?.assigned_group_id;
      if (assignedGroupId) {
        return proceedWithWhereby(assignedGroupId);
      }
      if (hasIndividualCourse(profile) && profile.owner_user_id) {
        return proceedWithWhereby(individualWebinarHolderId(profile.owner_user_id));
      }
      throw new Error("Patient has no assigned group or individual course");
    }),
    catchError((err): Observable<never> => {
      console.error("Patient webinar prepare failed", err);
      analytics.track(EVENT_NAMES.webinar_join_prepared, {
        webinar_id: webinarId!,
        outcome: "error",
      });
      loader.hide();
      toastr.error(
        transloco.translate("webinar.notReadyText"),
        transloco.translate("webinar.notReadyTitle"),
      );
      return EMPTY;
    }),
  );

  function proceedWithWhereby(
    assignedGroupId: string,
  ): Observable<PrepareWebinarResult | boolean> {
    const prepareWithRetry = (
      attempt: number,
    ): Observable<PrepareWebinarResult> => {
      return webinarService
        .prepareWebinar(assignedGroupId, webinarId!, false)
        .pipe(
          first(),
          switchMap((prepareResult) => {
            if (
              prepareResult.status === "repair_in_progress" &&
              attempt < MAX_RETRIES
            ) {
              return timer(RETRY_DELAY_MS).pipe(
                switchMap(() => prepareWithRetry(attempt + 1)),
              );
            }

            if (prepareResult.status === "repair_in_progress") {
              analytics.track(EVENT_NAMES.webinar_join_prepared, {
                webinar_id: webinarId!,
                group_id: analyticsGroupId(assignedGroupId),
                outcome: "error",
              });
              loader.hide();
              toastr.error(
                transloco.translate("webinar.notReadyText"),
                transloco.translate("webinar.notReadyTitle"),
              );
              return EMPTY;
            }

            analytics.track(EVENT_NAMES.webinar_join_prepared, {
              webinar_id: webinarId!,
              group_id: analyticsGroupId(assignedGroupId),
              outcome: "success",
            });
            groupService.clearGroupCache(assignedGroupId);
            return store.dispatch(new FetchGroupAction()).pipe(
              first(),
              map(() => prepareResult),
            );
          }),
        );
    };

    return prepareWithRetry(1).pipe(
      map((prepareResult) => {
        loader.hide();
        return prepareResult;
      }),
    );
  }
};
